import { useState, useEffect } from "react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Server, Box, Zap, AlertTriangle, Activity, Database, Globe, TrendingUp, Clock } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { DASHBOARD } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  healthy: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  warning: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  critical: "text-rose-400 bg-rose-400/10 border-rose-400/20",
};

function StatCard({ icon: Icon, label, value, sub, color = "text-slate-50", testId }) {
  return (
    <div data-testid={testId} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 hover:bg-slate-800/40 transition-colors duration-200 animate-fade-in">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={14} className="text-slate-600" strokeWidth={1.75} />
      </div>
      <div className={cn("text-2xl font-semibold tracking-tight", color)} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        {value}
      </div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function HealthScore({ score }) {
  const color = score >= 90 ? "#34d399" : score >= 70 ? "#fbbf24" : "#f87171";
  const pct = score;
  const r = 28, c = 2 * Math.PI * r;
  const stroke = c - (pct / 100) * c;
  return (
    <div data-testid={DASHBOARD.healthScore} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 flex items-center gap-4 animate-fade-in">
      <div className="relative w-20 h-20 flex-shrink-0">
        <svg viewBox="0 0 64 64" className="w-20 h-20 -rotate-90">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#1e293b" strokeWidth="4" />
          <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={c} strokeDashoffset={stroke} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 1s ease" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-base font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{score}</span>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Health Score</p>
        <p className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{score}/100</p>
        <p className="text-xs text-slate-400 mt-0.5">{score >= 90 ? "Excellent" : score >= 70 ? "Fair — investigate warnings" : "Critical — immediate action needed"}</p>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: <span className="font-mono font-medium">{p.value}%</span></p>
      ))}
    </div>
  );
};

const EventBadge = ({ type }) => (
  <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase",
    type === "Warning" ? "text-amber-400 bg-amber-400/10 border-amber-400/20" : "text-blue-400 bg-blue-400/10 border-blue-400/20"
  )}>{type}</span>
);

export default function Dashboard() {
  const { clusterId } = useCluster();
  const [overview, setOverview] = useState(null);
  const [cluster, setCluster] = useState(null);
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/overview`),
      axios.get(`${API}/clusters/${clusterId}`),
      axios.get(`${API}/clusters/${clusterId}/metrics/history?hours=12`),
      axios.get(`${API}/clusters/${clusterId}/events`),
      axios.get(`${API}/clusters/${clusterId}/alerts`),
    ]).then(([ov, cl, h, ev, al]) => {
      setOverview(ov.data);
      setCluster(cl.data);
      setHistory(h.data.slice(-24));
      setEvents(ev.data.slice(0, 5));
      setAlerts(al.data.slice(0, 4));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [clusterId]);

  if (loading || !cluster) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-24 bg-slate-800/50 rounded-md animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid={DASHBOARD.page} className="space-y-6">
      {/* Top stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Server} label="Clusters" value={overview?.clusters || 3} sub="All regions" testId={DASHBOARD.totalClusters} />
        <StatCard icon={Activity} label="Nodes" value={overview?.nodes || cluster.nodes_total} sub={`${cluster.nodes_ready} ready`} testId={DASHBOARD.totalNodes} />
        <StatCard icon={Box} label="Pods" value={overview?.pods || cluster.pods_total} sub={`${cluster.pods_running} running`} testId={DASHBOARD.totalPods} />
        <StatCard icon={Zap} label="Deployments" value={overview?.deployments || cluster.deployments_total} sub={`${cluster.deployments_ready} healthy`} testId={DASHBOARD.totalDeployments} />
        <StatCard icon={Database} label="Namespaces" value={overview?.namespaces || cluster.namespaces_count} sub="Active" />
        <StatCard icon={Globe} label="Services" value={overview?.services || cluster.services_count} sub="Exposed" />
      </div>

      {/* Health + Utilization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <HealthScore score={cluster.health_score} />

        <div data-testid={DASHBOARD.cpuUsage} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">CPU Usage</span>
            <span className="text-sm font-mono font-medium text-slate-300">{cluster.cpu_utilization}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{
              width: `${cluster.cpu_utilization}%`,
              background: cluster.cpu_utilization > 80 ? "#f87171" : cluster.cpu_utilization > 60 ? "#fbbf24" : "#34d399"
            }} />
          </div>
          <p className="text-xs text-slate-500 mt-2">{cluster.nodes_total} nodes · {(cluster.cpu_utilization / 100 * cluster.nodes_total * 8).toFixed(1)} cores used</p>
        </div>

        <div data-testid={DASHBOARD.memoryUsage} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Memory Usage</span>
            <span className="text-sm font-mono font-medium text-slate-300">{cluster.memory_utilization}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{
              width: `${cluster.memory_utilization}%`,
              background: cluster.memory_utilization > 85 ? "#f87171" : cluster.memory_utilization > 70 ? "#fbbf24" : "#34d399"
            }} />
          </div>
          <p className="text-xs text-slate-500 mt-2">{cluster.nodes_total} nodes · {(cluster.memory_utilization / 100 * cluster.nodes_total * 32).toFixed(0)} GB used</p>
        </div>
      </div>

      {/* Chart */}
      <div data-testid={DASHBOARD.metricsChart} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Resource Utilization (12h)</h3>
          <TrendingUp size={14} className="text-slate-600" />
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={history} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="timestamp" tick={{ fontSize: 10, fill: "#475569" }}
              tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              interval={Math.floor(history.length / 6)} />
            <YAxis tick={{ fontSize: 10, fill: "#475569" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="cpu" name="CPU" stroke="#6366f1" strokeWidth={2} fill="#6366f1" fillOpacity={0.08} />
            <Area type="monotone" dataKey="memory" name="Memory" stroke="#22d3ee" strokeWidth={2} fill="#22d3ee" fillOpacity={0.06} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Events + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Events */}
        <div data-testid={DASHBOARD.eventsWidget} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={13} className="text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Recent Events</h3>
          </div>
          <div className="space-y-2">
            {events.length === 0 && <p className="text-xs text-slate-500 py-2">No recent events</p>}
            {events.map((ev) => (
              <div key={ev.id} className="flex items-start gap-2 py-1.5 border-b border-slate-800/50 last:border-0">
                <EventBadge type={ev.type} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-300 truncate">{ev.message}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 font-mono">{ev.object} · {ev.namespace || "cluster"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        <div data-testid={DASHBOARD.alertsWidget} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={13} className="text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Active Alerts</h3>
            {alerts.length > 0 && (
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-rose-400/10 text-rose-400 border border-rose-400/20">{alerts.length}</span>
            )}
          </div>
          {alerts.length === 0 ? (
            <div className="flex items-center gap-2 py-4 text-xs text-emerald-400">
              <Activity size={12} />
              <span>All systems operational</span>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((al) => (
                <div key={al.id} className="flex items-start gap-2 py-1.5 border-b border-slate-800/50 last:border-0">
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase flex-shrink-0", statusColor[al.severity] || statusColor.warning)}>
                    {al.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-300 truncate font-medium">{al.title}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 truncate">{al.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
